/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import Home from './pages/Home.jsx'
import ProgramPage from './pages/ProgramPage.jsx'
import AboutPage from './pages/AboutPage.jsx'
import LinksPage from './pages/LinksPage.jsx'
import MoveOnPage from './pages/MoveOnPage.jsx'
import PrivacyPage from './pages/PrivacyPage.jsx'
import LibraryPage from './lib/LibraryPage.jsx'
import LibraryDetailPage from './lib/LibraryDetailPage.jsx'
import { EXERCISES, libPath, posterSrc } from './lib/data.js'
import ToolsHub from './pages/ToolsHub.jsx'
import { RpeToolPage, BmiToolPage, ToolRedirect } from './pages/ToolPage.jsx'
import fitness from './data/pages/fitness.json'
import cardio from './data/pages/cardio.json'
import corrective from './data/pages/corrective.json'
import flexibility from './data/pages/flexibility.json'
import mobility from './data/pages/mobility.json'
import warmCool from './data/pages/warm-cool.json'
import nutrition from './data/pages/nutritional-diet.json'

const INLINE = new Set(['strong', 'span', 'b', 'em', 'a', 'br', 'img'])
const textOf = (n) => (typeof n === 'string' ? n : (n.k || []).map(textOf).join(''))
const clean = (t) => t.replace(/\s+/g, ' ').trim()
const inlineOnly = (n) => typeof n === 'string' || (INLINE.has(n.t) && (n.k || []).every(inlineOnly))
const isInline = (n) => typeof n === 'string' || (n.k || []).every(inlineOnly)

function mdLines(n, out) {
  if (typeof n === 'string') {
    const t = clean(n)
    if (t) out.push(t)
    return
  }
  const t = clean(textOf(n))
  if (n.t === 'h1' || n.t === 'h2') return void (t && out.push('', '## ' + t, ''))
  if (n.t === 'h3' || n.t === 'h4') return void (t && out.push('', '### ' + t, ''))
  if (n.t === 'li') return void (t && out.push('- ' + t))
  if (isInline(n)) return void (t && !/^\d+$/.test(t) && out.push(t))
  for (const k of n.k || []) mdLines(k, out)
}

const programMarkdown = (data) => {
  const out = ['# ' + data.hero_text.title, '', data.hero_text.sub]
  for (const b of data.blocks) mdLines(b, out)
  out.push('', '## ' + data.faqTitle, '')
  for (const f of data.faq) out.push('### ' + f.q, '', clean(f.a.map(textOf).join(' ')), '')
  return out.join('\n').replace(/\n{3,}/g, '\n\n')
}

const program = (path, title, data, description, name) => ({
  path,
  title,
  description,
  kind: 'program',
  name,
  image: data.hero,
  heading: data.hero_text.title,
  faq: () => data.faq.map((f) => ({ q: f.q, a: clean(f.a.map(textOf).join(' ')) })),
  markdown: () => programMarkdown(data),
  Component: () => <ProgramPage data={data} />,
})

const REDIRECT_TOOLS = ['movement-screen', 'stopwatch', 'program-maker', 'water-reminder', 'period-reminder']

export const ROUTES = [
  { path: '/', title: 'سکویا فیتنس | برنامه‌ی تمرینی آنلاین برای خانه یا باشگاه', description: 'برنامه‌ساز و پلیر تمرین سکویا همراه آرشیو کامل تمرینی با بیش از ۵۰۰۰ تمرین و ۱۰۰۰ ویدیوی آموزشی، پشتیبانی مربی، پلنر و تقویم، ابزارهای تمرینی و مقاله‌های کاربردی؛ همه در یک پلتفرم.', kind: 'home', Component: Home },
  { path: '/about-sequoia/', title: 'درباره‌ی سکویا | سکویا فیتنس', description: 'داستان سکویا فیتنس؛ چرا یه پلتفرم آنلاین ساختیم تا بدون محدودیت زمان و مکان یه برنامه‌ی تمرینی اصولی داشته باشی و پیشرفتت رو ببینی.', kind: 'about', Component: AboutPage },
  program('/fitness-program/', 'برنامه تمرینی فیتنس شخصی برای خونه و باشگاه | سکویا فیتنس', fitness, 'برنامه فیتنس شخصی‌شده بر اساس هدف و سطح تو، با ویدیوی آموزشی هر تمرین و پشتیبانی مربی. برای خونه، باشگاه یا فضای باز؛ همه‌ی برنامه‌ت تو اپ سکویا.', 'برنامه تمرینی فیتنس'),
  program('/cardio-program/', 'برنامه کاردیو شخصی؛ چربی‌سوزی و تقویت قلب | سکویا فیتنس', cardio, 'برنامه کاردیو اختصاصی با LISS، HIIT و تمرین با دستگاه برای چربی‌سوزی، قلب قوی‌تر و انرژی بیشتر. تو خونه، باشگاه یا بیرون؛ همه‌ی برنامه‌ت تو اپ سکویا.', 'برنامه تمرینی کاردیو'),
  program('/corrective-program/', 'برنامه تمرین اصلاحی برای کمتر شدن درد و بهتر شدن قامت | سکویا فیتنس', corrective, 'برنامه تمرین اصلاحی شخصی با بررسی خانگی، ویدیوی آموزشی و پشتیبانی مربی؛ برای کمتر شدن دردهای رایج، اصلاح قامت و تمرین راحت‌تر.', 'برنامه تمرینی اصلاحی'),
  program('/flexibility-program/', 'برنامه انعطاف‌پذیری و کشش برای بدن نرم‌تر | سکویا فیتنس', flexibility, 'برنامه انعطاف‌پذیری شخصی با کشش ایستا، پویا و فعال برای کمتر شدن سفتی، ریکاوری بهتر و تمرین راحت‌تر؛ همراه ویدیو و PDF.', 'برنامه تمرینی انعطاف‌پذیری'),
  program('/mobility-program/', 'برنامه موبیلیتی برای مفصل‌های روان و تمرین بهتر | سکویا فیتنس', mobility, 'برنامه موبیلیتی شخصی برای باز شدن لگن، شونه و مچ پا و اسکات و تمرین‌های بالای سر راحت‌تر؛ با ویدیوی آموزشی و پشتیبانی.', 'برنامه تمرینی موبیلیتی'),
  program('/warm-cool-program/', 'برنامه گرم‌کردن و سردکردن برای تمرین امن‌تر | سکویا فیتنس', warmCool, 'گرم‌کردن و سردکردن اختصاصی برای هر جلسه تمرین تا کمتر آسیب ببینی، کمتر کوفته بشی و سریع‌تر ریکاوری کنی؛ همراه ویدیو و PDF.', 'برنامه گرم‌کردن و سردکردن'),
  program('/nutritional-diet-program/', 'برنامه غذایی ورزشی شخصی‌شده | سکویا فیتنس', nutrition, 'برنامه غذایی ورزشی بر اساس هدف و سبک زندگی تو، با غذاهای ساده و آشنا، همراه برنامه تمرینی و پشتیبانی؛ برای چربی‌سوزی یا عضله‌سازی.', 'برنامه غذایی ورزشی'),
  { path: '/links/', title: 'سکویا فیتنس | تماس با ما', description: 'لینک‌های رسمی سکویا فیتنس؛ سفارش برنامه، اپ و شبکه‌های اجتماعی.', image: '/a/og-links-v1.png', kind: 'page', Component: LinksPage },
  { path: '/move-on/', title: 'انتقال به پلتفرم جدید سکویا فیتنس', description: 'راهنمای انتقال به پلتفرم جدید سکویا فیتنس و ورود به اپ.', kind: 'page', noindex: true, Component: MoveOnPage },
  { path: '/sequoia-tools/', title: 'جعبه‌ابزار تمرین و سلامت | سکویا فیتنس', description: 'ابزارهای رایگان سکویا برای سنجش، برنامه‌ریزی و پیگیری تمرین: محاسبه‌گر 1RM و پیشنهاد وزنه و BMI، تایمر، یادآور آب و برنامه‌ساز.', kind: 'page', Component: ToolsHub },
  { path: '/sequoia-tools/rpecalculator/', title: 'محاسبه‌گر 1RM و پیشنهاد وزنه | سکویا فیتنس', description: 'یک تکرار بیشینه (1RM) و شدت تمرینت رو با مقیاس RPE و RIR حساب کن و وزنه‌های ست‌هات رو هوشمند تنظیم کن.', kind: 'tool', name: 'محاسبه‌گر 1RM و پیشنهاد وزنه', Component: RpeToolPage },
  { path: '/sequoia-tools/bmi-calculator/', title: 'محاسبه‌گر BMI | سکویا فیتنس', description: 'شاخص توده بدنی (BMI)، محدوده‌ی وزن مناسب و وضعیت تناسب اندامت رو سریع و بر اساس استاندارد WHO بسنج.', kind: 'tool', name: 'محاسبه‌گر BMI', Component: BmiToolPage },
  ...REDIRECT_TOOLS.map((slug) => ({ path: '/sequoia-tools/' + slug + '/', title: 'سکویا فیتنس', kind: 'redirect', Component: () => <ToolRedirect slug={slug} /> })),
  { path: '/sequoia-library/', title: 'کتابخانه تمرین | سکویا فیتنس', description: 'کتابخانه‌ی تمرین‌های سکویا با ویدیوی آموزشی، عضله‌های درگیر، وسیله‌ی لازم و نکته‌های اجرا برای هر تمرین.', kind: 'page', Component: LibraryPage },
  ...EXERCISES.map((e) => ({
    path: libPath(e.id),
    title: e.title + ' | سکویا فیتنس',
    description: (e.guide && e.guide.description) || 'ویدیوی آموزشی و توضیح اجرای تمرین ' + e.title + ' با نکته‌های فرم درست؛ از کتابخانه‌ی تمرین سکویا.',
    kind: 'exercise',
    name: e.title,
    image: posterSrc(e.id),
    Component: () => <LibraryDetailPage id={e.id} />,
  })),
  { path: '/privacy-policy/', title: 'قوانین و مقررات | سکویا فیتنس', description: 'قوانین و مقررات استفاده از خدمات سکویا فیتنس و حریم خصوصی کاربران.', kind: 'page', noindex: true, Component: PrivacyPage },
]

export function matchRoute(pathname) {
  const p = pathname.endsWith('/') ? pathname : pathname + '/'
  return ROUTES.find((r) => r.path === p) || ROUTES[0]
}
